<app-menu-common url="sales"></app-menu-common>

<div class="bank-lettering-container">
  <!-- Top Page Header & Title Row -->
  <div class="pageheader flexcolumn">
    <div class="header-title-row">
      <div class="pagetitle-wrapper">
        <mat-icon class="pagetitle-icon">account_balance</mat-icon>
        <div>
          <div class="pagetitle grey">Rapprochement Bancaire & Lettrage Ventes</div>
          <div class="pagesubtitle text-muted">Pointer un encaissement bancaire et solder les créances clients (BOFIP ISCA)</div>
        </div>
      </div>
      <span class="spacer"></span>
      <div class="header-actions">
        <a mat-stroked-button color="primary" routerLink="/sales/invoices" class="action-btn" matTooltip="Consulter le registre des factures">
          <mat-icon>receipt_long</mat-icon>
          Factures Clients
        </a>
        <button mat-stroked-button color="primary" class="action-btn" (click)="loadOpenReceivables()" [disabled]="isLoading" matTooltip="Rafraîchir la liste des créances">
          <mat-icon [class.spinning]="isLoading">refresh</mat-icon>
          Actualiser
        </button>
      </div>
    </div>

    <!-- Zone 1 : Saisie de l'encaissement bancaire -->
    <div class="statement-card">
      <div class="section-title">
        <mat-icon>payment</mat-icon>
        <span>Zone 1 : Saisie de la ligne bancaire / relevé</span>
        <span class="spacer"></span>
        <!-- Poste d'arrivée calculé par le serveur pour le mode choisi : le poste courant, ou le compte bancaire pour un virement. -->
        <span class="station-badge" [class.station-refused]="!!routingRefusal" [matTooltip]="routingRefusal ?? (targetLabel + ' : ' + targetHint)">
          <mat-icon class="badge-icon">{{ isBankMethod ? 'account_balance' : 'point_of_sale' }}</mat-icon>
          {{ targetLabel }}
        </span>
      </div>

      <div class="filters-row">
        <!-- Date bancaire -->
        <div class="filter-item date-filter">
          @if (isBankMethod) {
            <app-select-date
              label="Date de règlement"
              [date]="statement.dateReglement"
              (dateChange)="onDateChange($event)">
            </app-select-date>
          } @else {
            <label class="filter-label">Date de règlement</label>
            <span class="date-now">Encaissé maintenant</span>
          }
        </div>

        <!-- Mode de règlement -->
        <div class="filter-item method-filter">
          <label class="filter-label">Mode de règlement</label>
          <select class="custom-select" [(ngModel)]="statement.paymentMethod">
            @for (method of routing?.methods ?? []; track method.id) {
              <option [ngValue]="method.id" [disabled]="!!method.refusal" [attr.title]="method.refusal">{{ method.name }}{{ method.refusal ? ' (indisponible sur ce poste)' : '' }}</option>
            }
          </select>
        </div>

        <!-- Compte crédité : choisi seulement s'il y a plusieurs postes banque, l'unique est pris d'office. -->
        @if (isBankMethod && (routing?.bank_stations ?? []).length > 1) {
          <div class="filter-item bank-station-filter">
            <label class="filter-label">Compte crédité</label>
            <select class="custom-select" [(ngModel)]="statement.bankStation">
              @for (bank of routing?.bank_stations ?? []; track bank.id) {
                <option [ngValue]="bank.id">{{ bank.name ?? ('#' + bank.id) }}</option>
              }
            </select>
          </div>
        }

        <!-- Référence relevé / virement -->
        <div class="filter-item ref-filter">
          <label class="filter-label">{{ referenceLabel }}</label>
          <input
            type="text"
            class="custom-input"
            placeholder="Ex: VIR-CPAM-20260827, CHQ-10492..."
            [(ngModel)]="statement.bankReference" />
        </div>

        <!-- Compte / IBAN bancaire : un virement seulement, le serveur le refuse pour un règlement remis en main propre -->
        @if (isBankMethod) {
          <div class="filter-item iban-filter">
            <label class="filter-label">Compte bancaire / IBAN (optionnel)</label>
            <input
              type="text"
              class="custom-input"
              placeholder="Ex: FR76 3000 4000..."
              [(ngModel)]="statement.bankAccount" />
          </div>
        }

        <!-- Montant total encaissé -->
        <div class="filter-item amount-filter">
          <label class="filter-label highlight-label">
            Montant encaissé relevé (€)
            @if (method?.arrival === 'DRAWER') {
              <span class="amount-hint">montant net laissé au tiroir (monnaie rendue déduite)</span>
            }
          </label>
          <div class="amount-input-wrapper">
            <input
              type="number"
              step="0.01"
              class="custom-input amount-input"
              placeholder="0.00"
              [(ngModel)]="statement.totalAmount" />
            <span class="currency-symbol">€</span>
          </div>
        </div>
      </div>

      <!-- Note / Commentaire -->
      <div class="statement-footer-row">
        <div class="filter-item full-width">
          <label class="filter-label">Note / Motif du lettrage (optionnel)</label>
          <input
            type="text"
            class="custom-input"
            placeholder="Commentaire interne pour la piste d'audit..."
            [(ngModel)]="statement.note" />
        </div>
      </div>
    </div>
  </div>

  <!-- Zone 3 : Contrôle d'équilibrage en temps réel (KPI Banner & Action Bar) -->
  <div class="kpi-banner">
    <!-- Card 1 : Montant bancaire saisi -->
    <div class="kpi-card">
      <div class="kpi-icon-wrapper purple">
        <mat-icon>account_balance_wallet</mat-icon>
      </div>
      <div class="kpi-content">
        <div class="kpi-value">{{ (statement.totalAmount || 0) | number:'1.2-2' }} €</div>
        <div class="kpi-label">Montant Relevé Saisi</div>
      </div>
    </div>

    <!-- Card 2 : Total alloué aux créances -->
    <div class="kpi-card">
      <div class="kpi-icon-wrapper blue">
        <mat-icon>playlist_add_check</mat-icon>
      </div>
      <div class="kpi-content">
        <div class="kpi-value">{{ getTotalAllocated() | number:'1.2-2' }} €</div>
        <div class="kpi-label">Total Imputé ({{ selectedReceivableIds.size }} créance{{ selectedReceivableIds.size > 1 ? 's' : '' }})</div>
      </div>
    </div>

    <!-- Card 3 : Écart d'équilibrage -->
    <div class="kpi-card" [class.highlight-balanced]="isBalanced()" [class.highlight-imbalance]="!isBalanced() && (statement.totalAmount || 0) > 0">
      <div class="kpi-icon-wrapper" [ngClass]="isBalanced() ? 'green' : 'red'">
        <mat-icon>{{ isBalanced() ? 'check_circle' : 'balance' }}</mat-icon>
      </div>
      <div class="kpi-content">
        <div class="kpi-value" [ngClass]="isBalanced() ? 'success-text' : 'due-alert'">
          {{ getBalanceDifference() | number:'1.2-2' }} €
        </div>
        <div class="kpi-label">
          {{ isBalanced() ? 'Équilibré (0,00 €) - Prêt à valider' : (getBalanceDifference() > 0 ? 'Reste à imputer sur créances' : 'Dépassement du montant bancaire') }}
        </div>
      </div>
    </div>

    <!-- Card 4 : Validation & Déclenchement Scellement BOFIP -->
    <div class="kpi-card validation-card">
      <div class="validation-content">
        <button
          mat-flat-button
          color="primary"
          class="validate-button"
          (click)="onSubmitLettering()"
          [disabled]="!isBalanced() || isSubmitting || !!routingRefusal">
          <mat-icon>lock</mat-icon>
          <span>Valider et sceller BOFIP</span>
        </button>
        <!-- Le refus de poste passe en premier : sur une saisie équilibrée, il est la seule raison du bouton grisé (lue par la NRT). -->
        @if (routingRefusal) {
          <div class="validation-hint refused">
            <span>{{ routingRefusal }}</span>
          </div>
        } @else if (!isBalanced()) {
          <div class="validation-hint">
            @if ((statement.totalAmount || 0) <= 0) {
              <span>Saisir le montant bancaire</span>
            }
            @if ((statement.totalAmount || 0) > 0 && selectedReceivableIds.size === 0) {
              <span>Sélectionner des créances</span>
            }
            @if ((statement.totalAmount || 0) > 0 && selectedReceivableIds.size > 0 && getBalanceDifference() !== 0) {
              <span>
                Ajuster pour atteindre 0,00 € d'écart
              </span>
            }
          </div>
        } @else {
          <div class="validation-hint success">
            <span>Équilibrage parfait : prêt au scellement fiscal</span>
          </div>
        }
      </div>
    </div>
  </div>

  <!-- Zone 2 : Sélecteur de créances à lettrer -->
  <div class="table-card">
    <div class="table-header-bar">
      <div class="table-title">
        <mat-icon>list_alt</mat-icon>
        <span>Zone 2 : Sélection des créances ouvertes à imputer</span>
        <span class="badge-count">{{ receivables.length }} créance{{ receivables.length > 1 ? 's' : '' }}</span>
      </div>

      <!-- Quick Filters on Receivables -->
      <div class="table-filters-inline">
        <div class="filter-item-inline partner-box">
          <app-search-partner
            [currentPartner]="selectedPartner"
            width="320px"
            [showSelectionInInput]="true"
            (partnerChange)="onPartnerChange($event)">
          </app-search-partner>
        </div>

        <div class="filter-item-inline search-box">
          <input
            type="text"
            class="custom-input small-input"
            placeholder="Filtrer réf facture, commande, tiers..."
            [(ngModel)]="filter.search"
            (keyup.enter)="onSearch()" />
          @if (filter.search) {
            <button mat-icon-button (click)="filter.search = ''; onSearch()" class="clear-btn">
              <mat-icon>close</mat-icon>
            </button>
          }
        </div>

        <button mat-flat-button color="primary" (click)="onSearch()" [disabled]="isLoading" class="btn-search-inline">
          <mat-icon>search</mat-icon>
          Filtrer
        </button>

        <button mat-stroked-button (click)="onResetFilters()" [disabled]="isLoading" class="btn-reset-inline" matTooltip="Réinitialiser filtres">
          <mat-icon>clear</mat-icon>
        </button>

        <!-- FSE Third-Party Payer (AMO / AMC) Filter Toggle -->
        <div class="filter-item-inline tp-toggle-box">
          <mat-checkbox
            [(ngModel)]="filter.includeTp"
            (change)="onSearch()"
            color="primary"
            matTooltip="Afficher également les créances Tiers-Payeur FSE (AMO Sécurité Sociale / AMC Mutuelle)">
            Inclure Tiers-Payeur FSE (AMO/AMC)
          </mat-checkbox>
        </div>

        <span class="spacer"></span>

        <!-- Batch Selection Actions -->
        <div class="batch-actions">
          <button mat-button color="primary" (click)="selectAllReceivables()" [disabled]="receivables.length === 0">
            <mat-icon>select_all</mat-icon>
            Tout sélectionner
          </button>
          <button mat-button (click)="deselectAllReceivables()" [disabled]="selectedReceivableIds.size === 0">
            <mat-icon>deselect</mat-icon>
            Désélectionner
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    @if (isLoading) {
      <div class="loading-state">
        <mat-spinner diameter="40"></mat-spinner>
        <span>Chargement des créances ouvertes...</span>
      </div>
    }

    <!-- Receivables Table -->
    @if (!isLoading) {
      <div class="table-responsive">
        @if (receivables.length > 0) {
          <table class="lettering-table">
            <thead>
              <tr>
                <th class="col-checkbox text-center">
                  <mat-icon class="th-icon">check_box</mat-icon>
                </th>
                <th class="col-docref">Réf. Facture</th>
                <th class="col-origin">Commande / BL</th>
                <th class="col-date">Date Émission</th>
                <th class="col-payer">Tiers Payeur</th>
                <th class="col-type text-center">Type</th>
                <th class="col-amount text-right">Total Attendu</th>
                <th class="col-amount text-right">Déjà Réglé</th>
                <th class="col-amount text-right">Reste Dû</th>
                <th class="col-state text-center">Statut</th>
                <th class="col-allocation text-right">Montant à Imputer (€)</th>
                <th class="col-quick-action text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (rec of receivables; track trackByReceivable($index, rec)) {
                <tr
                  class="receivable-row"
                  [class.row-selected]="isReceivableSelected(rec.id)"
                  [class.row-invalid]="isAllocationInvalid(rec)"
                  [class.row-disabled]="!isReceivableSelectable(rec)">
                  <!-- Checkbox -->
                  <td class="col-checkbox text-center">
                    <mat-checkbox
                      [checked]="isReceivableSelected(rec.id)"
                      [disabled]="!isReceivableSelectable(rec)"
                      (change)="toggleReceivableSelection(rec)"
                      [matTooltip]="!isReceivableSelectable(rec) ? (getUninvoicedLabel(rec) + ' : non lettrable (facture requise)') : ''"
                      color="primary">
                    </mat-checkbox>
                  </td>
                  <!-- Doc Reference -->
                  <td class="col-docref">
                    @if (rec.docRef) {
                      <span class="docref-chip">
                        <mat-icon class="pdf-icon">receipt</mat-icon>
                        <span>{{ rec.docRef }}</span>
                      </span>
                    }
                    @if (!rec.docRef) {
                      <span class="text-muted font-italic">
                        {{ getUninvoicedLabel(rec) }}
                      </span>
                    }
                  </td>
                  <!-- Order / Delivery ID -->
                  <td class="col-origin">
                    <div class="origin-links">
                      <a [routerLink]="['/sales/quotation', rec.orderId]" class="link-order" matTooltip="Ouvrir la commande">
                        Cde #{{ rec.orderId }}
                      </a>
                      @if (rec.deliveryId) {
                        <span class="badge-delivery" matTooltip="Bon de livraison">
                          BL #{{ rec.deliveryId }}
                        </span>
                      }
                    </div>
                  </td>
                  <!-- Invoice Date -->
                  <td class="col-date">
                    {{ rec.invoiceDate ? (rec.invoiceDate | date:'dd/MM/yyyy') : '-' }}
                  </td>
                  <!-- Payer Name -->
                  <td class="col-payer">
                    <div class="payer-name">{{ rec.payerName }}</div>
                    <div class="payer-id text-muted">ID Tiers : {{ rec.payerId }}</div>
                  </td>
                  <!-- Payer Type (AMO / AMC / CLIENT) -->
                  <td class="col-type text-center">
                    <span class="badge-payer-type" [ngClass]="getPayerTypeBadgeClass(rec.payerType)">
                      {{ rec.payerType || 'CLIENT' }}
                    </span>
                  </td>
                  <!-- Total Expected -->
                  <td class="col-amount text-right">
                    {{ rec.amountExpected | number:'1.2-2' }} €
                  </td>
                  <!-- Amount Paid -->
                  <td class="col-amount text-right success-text">
                    {{ rec.amountPaid | number:'1.2-2' }} €
                  </td>
                  <!-- Amount Due -->
                  <td class="col-amount text-right due-highlight">
                    <span>{{ rec.amountDue | number:'1.2-2' }} €</span>
                  </td>
                  <!-- State Badge -->
                  <td class="col-state text-center">
                    <span class="status-badge" [ngClass]="rec.state === 'PARTIALLY_PAID' ? 'badge-partial' : 'badge-open'">
                      <mat-icon class="badge-icon">{{ rec.state === 'PARTIALLY_PAID' ? 'timelapse' : 'error_outline' }}</mat-icon>
                      {{ rec.state === 'PARTIALLY_PAID' ? 'Partielle' : 'Ouverte' }}
                    </span>
                  </td>
                  <!-- Allocated Amount Input -->
                  <td class="col-allocation text-right">
                    <div class="allocation-input-wrapper">
                      <input
                        type="number"
                        step="0.01"
                        class="custom-input alloc-input"
                        [class.alloc-active]="isReceivableSelected(rec.id)"
                        [class.alloc-error]="isAllocationInvalid(rec)"
                        [disabled]="!isReceivableSelectable(rec)"
                        [ngModel]="getAllocatedAmount(rec.id)"
                        (ngModelChange)="onAllocationChange(rec, $event)" />
                      <span class="currency-label">€</span>
                    </div>
                    @if (isAllocationInvalid(rec)) {
                      <div class="error-hint">
                        @if (getAllocatedAmount(rec.id) === 0) {
                          <span>Montant requis non nul</span>
                        }
                        @if (rec.amountDue > 0 && (getAllocatedAmount(rec.id) < 0 || getAllocatedAmount(rec.id) > rec.amountDue)) {
                          <span>Dépasse le solde ({{ rec.amountDue | number:'1.2-2' }} €) ou mauvais signe</span>
                        }
                        @if (rec.amountDue <= 0 && (getAllocatedAmount(rec.id) > 0 || getAllocatedAmount(rec.id) < rec.amountDue)) {
                          <span>Dépasse le solde ({{ rec.amountDue | number:'1.2-2' }} €) ou mauvais signe</span>
                        }
                      </div>
                    }
                  </td>
                  <!-- Quick Action Buttons -->
                  <td class="col-quick-action text-center">
                    <div class="quick-actions-row">
                      <button
                        mat-stroked-button
                        class="btn-solde"
                        [disabled]="!isReceivableSelectable(rec)"
                        (click)="setFullBalance(rec)"
                        matTooltip="Imputer la totalité du reste dû ({{ rec.amountDue | number:'1.2-2' }} €)">
                        Solde max
                      </button>
                      <button
                        mat-icon-button
                        class="btn-solde-rem"
                        [disabled]="!isReceivableSelectable(rec)"
                        (click)="setRemainingBankAmount(rec)"
                        matTooltip="Imputer le reliquat bancaire disponible">
                        <mat-icon>arrow_downward</mat-icon>
                      </button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        } @else {
          <div class="empty-state">
            <mat-icon class="empty-icon">check_circle_outline</mat-icon>
            <div class="empty-title">Aucune créance ouverte en attente</div>
            <div class="empty-subtitle">
              Toutes les factures et créances sont intégralement soldées pour les critères sélectionnés.
            </div>
            <button mat-stroked-button color="primary" (click)="onResetFilters()">
              Réinitialiser les filtres
            </button>
          </div>
        }
        <!-- Empty State -->
      </div>
    }
  </div>
</div>
